<!--
 * @Description: 
 * @Date: 2025-09-01 10:22:20
 * @LastEditTime: 2026-11-05 16:58:54
 * @FilePath: \hui_ai_ui\src\App.vue
-->
<template>
  <div id="app">
    <LoadingOverlay :visible="appLoading" text="加载中..." />
    <router-view />
    <!-- 悬浮聊天组件 - 根据路由判断是否显示 -->
    <FloatingChat v-if="showFloatingChat && !isMobileWidth" :user-id="userId" @drawer-changed="handleDrawerChanged" />
    <!-- 登录对话框 -->
    <LoginDialog v-if="showLoginDialog" />
    <!-- Web端登录对话框 -->
    <LoginDialogWeb v-if="showLoginWebDialog" />
    <!-- 重置密码对话框 -->
    <ResetPasswordDialog v-if="showResetPasswordDialog" />
    <!-- 全局确认弹窗（移动端适配） -->
    <MobileConfirm :visible.sync="showGlobalConfirm" :title="globalConfirmTitle" :message="globalConfirmMessage"
      @confirm="handleGlobalConfirm" />
  </div>
</template>

<script>
import FloatingChat from "./views/AI/agent/components/FloatingChat.vue";
import LoginDialog from "./components/LoginDialog.vue";
import LoginDialogWeb from "./components/LoginDialogWeb.vue";
import ResetPasswordDialog from "./components/ResetPasswordDialog.vue";
import MobileConfirm from "./components/MobileConfirm.vue";
import LoadingOverlay from "./views/AI/components/LoadingOverlay.vue";
import { getCurrentWindow } from '@tauri-apps/api/window';
import { isTauri } from '@/config';
import { createSSEConnection } from "./api/ai/chat.js";
import eventBus from "./utils/eventBus.js";
export default {
  name: "App",
  components: {
    FloatingChat,
    LoginDialog,
    LoginDialogWeb,
    ResetPasswordDialog,
    MobileConfirm,
    LoadingOverlay
  },
  data() {
    return {
      appLoading: true,
      isMobileWidth: false,
      _resizeHandler: null,
      sseConnection: null, // SSE连接实例
      sseReconnectTimer: null, // 重连定时器
      sseConnected: false, // SSE连接状态
      sseReconnectAttempts: 0, // 重连尝试次数
      sseEverConnected: false, // 是否已成功连接过SSE（用于防止重复连接）
      refreshChatTimer: null // 刷新聊天历史的防抖定时器
    };
  },
  computed: {
    userId() {
      return this.$store.state.userId || '';
    },
    showFloatingChat() {
      const path = this.$route.path;
      const name = this.$route.name;
      // 如果路由还未完全解析（path为/且name为null），暂时不渲染
      if (path === '/' && name === null) {
        return false;
      }
      // 未登录（没有userId）不展示
      if (!this.userId) {
        return false;
      }
      // /ai/agent/voicemg不显示
      if (path === '/ai/agent/voicemg' || path.includes('/ai/mchat') || path.includes('/ai/tech_chat') || path.includes('/ai/notary_chat')) {
        return false; // 管理页不展示客服
      }
      // Tauri环境下/ai/preview-modal不显示
      if (typeof isTauri !== 'undefined' && isTauri && path === '/ai/preview-modal') {
        return false; // Tauri预览页不展示客服
      }
      return true;
    },
    showLoginDialog() {
      return this.$store.getters.showLoginDialog;
    },
    showLoginWebDialog() {
      return this.$store.getters.showLoginWebDialog;
    },
    showResetPasswordDialog() {
      return this.$store.getters.showResetPasswordDialog;
    },
    showGlobalConfirm: {
      get() {
        return this.$store.getters.showGlobalConfirm;
      },
      set(value) {
        if (value === false) {
          this.$store.commit('HIDE_GLOBAL_CONFIRM');
        }
      }
    },
    globalConfirmTitle() {
      return this.$store.getters.globalConfirmTitle;
    },
    globalConfirmMessage() {
      return this.$store.getters.globalConfirmMessage;
    }
  },
  created() {
    this._resizeHandler = () => this.checkMobileWidth();
    this.checkMobileWidth();
    window.addEventListener('resize', this._resizeHandler);
    
    // 提前检查路由，如果是 /ai/mchat 开头的路由，标记已连接过以避免建立 SSE 连接
    // 使用 window.location.pathname 获取真实 URL，避免 Vue Router 未初始化时获取不到正确路由
    const currentPath = window.location.pathname;
    const noSseRegex = /^\/ai\/(mchat|notary_chat)/;
    // console.log('[SSE] 当前URL:', currentPath, '匹配结果:', noSseRegex.test(currentPath));

    if (noSseRegex.test(currentPath)) {
      this.sseEverConnected = true;
      // console.log('[SSE] 当前路由为 mchat/notary_chat，预先标记已连接状态，避免建立 SSE 连接');
    }
    
    // 初始化用户（SSE 连接由 store.subscribe 处理，避免重复调用）
    this.$store.dispatch("initializeUser").catch((error) => {
      console.log('初始化用户失败:', error);
    });

    // Update page title with version from localStorage
    try {
      const version = localStorage.getItem('version');
      if (version) {
        document.title = `汇AI ${version}`;
      }
    } catch (error) {
      console.error('Failed to update title from localStorage:', error);
    }

    if (isTauri) {
      // Update Tauri desktop title
      this.updateDesktopTitle();

      // 禁用F5刷新快捷键（开发和生产环境都禁用）
      this._disableRefreshHandler = (e) => {
        if (e.key === 'F5' || e.keyCode === 116 ||
          (e.key === 'r' && (e.ctrlKey || e.metaKey))) {
          e.preventDefault();
          e.stopPropagation();
          console.log('F5刷新已被禁用');
        }
      };
      window.addEventListener('keydown', this._disableRefreshHandler, { capture: true });
    }

    // 监听用户登录状态变化
    this.$store.subscribe((mutation) => {
      if (mutation.type === 'SET_USER_ID' || mutation.type === 'SET_USER_INFO') {
        // 用户登录状态变化（包括更换账号），重置连接状态并尝试重新建立 SSE 连接
        // 使用 window.location.pathname 获取真实 URL，避免 Vue Router 未初始化时获取不到正确路由
        const currentPath = window.location.pathname;
        const noSseRegex = /^\/ai\/(mchat|notary_chat)/;
        
        // 如果是 mchat/notary_chat 路由，不重置 sseEverConnected，保持跳过连接状态
        if (!noSseRegex.test(currentPath)) {
          this.sseEverConnected = false; // 重置连接标记，允许重新连接
          setTimeout(() => {
            if (!this.sseConnected) {
              this.connectSSE();
            }
          }, 1000);
        }
      }
    });

    // 监听路由变化，处理 SSE 连接
    this.$router.afterEach((to) => {
      const noSseRegex = /^\/ai\/(mchat|notary_chat)/;
      if (noSseRegex.test(to.path)) {
        // console.log('[SSE] 路由切换到 mchat/notary_chat，关闭 SSE 连接');
        this.disconnectSSE();
      } else if (!this.sseEverConnected && !this.sseConnected) {
        // 从 mchat 切换到其他路由，且之前没有连接过 SSE，才尝试连接
        setTimeout(() => {
          this.connectSSE();
        }, 500);
      }
    });
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.appLoading = false;
      }, 300);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._resizeHandler);
    // 清理F5禁用事件监听器
    if (this._disableRefreshHandler) {
      window.removeEventListener('keydown', this._disableRefreshHandler, { capture: true });
    }
    // 关闭 SSE 连接
    this.disconnectSSE();
  },
  methods: {
    checkMobileWidth() {
      this.isMobileWidth = window.innerWidth < 500;
    },
    handleDrawerChanged(visible) {
      // 处理抽屉状态变化
    },
    handleGlobalConfirm() {
      const callback = this.$store.state.globalConfirmCallback;
      this.$store.commit('HIDE_GLOBAL_CONFIRM');
      if (callback) {
        callback();
      }
    },
    // 初始化 SSE 连接
    connectSSE() {
      const userId = this.userId;
      if (!userId) {
        // console.log('[SSE] 用户未登录，无法建立连接');
        return;
      }

      // 判断当前路由是否为 /ai/mchat 或 /ai/notary_chat 开头的路由，这些路由使用 WebSocket，不需要 SSE
      // 使用 window.location.pathname 获取真实 URL，避免 Vue Router 未初始化时获取不到正确路由
      const currentPath = window.location.pathname;
      const noSseRegex = /^\/ai\/(mchat|notary_chat)/;
      // console.log('[SSE] 当前URL:', currentPath, '匹配结果:', noSseRegex.test(currentPath));
      if (noSseRegex.test(currentPath)) {
        // console.log('[SSE] 当前路由为 mchat/notary_chat，使用 WebSocket，跳过 SSE 连接');
        return;
      }

      // 如果已有连接，先关闭
      this.disconnectSSE();

      try {
        const es = createSSEConnection(userId);
        this.sseConnection = es;

        // 连接打开
        es.onopen = () => {
          //console.log('[SSE] 连接已建立');
          this.sseConnected = true;
          this.sseReconnectAttempts = 0;
          this.sseEverConnected = true; // 标记已成功连接过
        };

        // 默认消息（无 event 字段时触发）
        es.onmessage = (event) => {
          console.log('[SSE] onmessage 收到推送:', event.data);
          try {
            const data = JSON.parse(event.data);
            this.handleSSEMessage(data);
          } catch (err) {
            console.log('[SSE] 消息解析失败:', err, '原始数据:', event.data);
          }
        };

        // 监听自定义事件: offline_message（离线消息）
        es.addEventListener('offline_message', (event) => {
          console.log('[SSE] offline_message:', event.data);
          try {
            const data = JSON.parse(event.data);
            this.handleOfflineMessage(data);
          } catch (err) {
            console.warn('[SSE] offline_message 处理失败:', err);
          }
        });

        // 监听自定义事件: connected（连接成功）
        es.addEventListener('connected', (event) => {
          // console.log('[SSE] connected:', event.data);
          try {
            const data = JSON.parse(event.data);
            this.handleConnected(data);
          } catch (err) {
            //console.warn('[SSE] connected 解析失败:', err);
          }
        });

        // 监听自定义事件: ping（心跳）
        es.addEventListener('ping', (event) => {
          try {
            // ping 事件可能是 JSON 格式或纯文本格式
            let data;
            if (event.data && typeof event.data === 'string') {
              // 尝试解析为 JSON，如果失败则作为纯文本处理
              try {
                data = JSON.parse(event.data);
              } catch (jsonErr) {
                // 非 JSON 格式，直接作为数据传递
                data = { timestamp: event.data };
              }
            }
            this.handlePing(data);
          } catch (err) {
            console.warn('[SSE] ping 处理失败:', err);
          }
        });

        // 监听自定义事件: task_failed（任务失败）
        es.addEventListener('task_failed', (event) => {
          console.log('[SSE] task_failed:', event.data);
          try {
            const data = JSON.parse(event.data);
            this.handleTaskFailed(data);
          } catch (err) {
            console.warn('[SSE] task_failed 解析失败:', err);
          }
        });

        // 监听自定义事件: task_completed（任务完成）
        es.addEventListener('task_completed', (event) => {
          // console.log('[SSE] task_completed:', event.data);
          try {
            const data = JSON.parse(event.data);
            this.handleTaskCompleted(data);
          } catch (err) {
            console.warn('[SSE] task_completed 解析失败:', err);
          }
        });

        // 监听自定义事件: business_count（业务统计）
        es.addEventListener('business_count', (event) => {
          // console.log('[SSE] business_count:', event.data);
          try {
            const data = JSON.parse(event.data);
            this.handleBusinessCount(data);
          } catch (err) {
            console.warn('[SSE] business_count 解析失败:', err);
          }
        });

        // 连接错误
        es.onerror = (err) => {
          // console.log('[SSE] 连接错误:', err);
          this.sseConnected = false;
          es.close();
          this.sseConnection = null;
          // 自动重连
          this.scheduleSSEReconnect();
        };

      } catch (error) {
        console.error('[SSE] 创建连接失败:', error);
        this.scheduleSSEReconnect();
      }
    },
    // 关闭 SSE 连接
    disconnectSSE() {
      if (this.sseConnection) {
        try {
          this.sseConnection.close();
          // console.log('[SSE] 已关闭');
        } catch (error) {
          // console.error('[SSE] 关闭失败:', error);
        }
        this.sseConnection = null;
      }
      this.sseConnected = false;
      clearTimeout(this.sseReconnectTimer);
      this.sseReconnectTimer = null;
    },
    // SSE 自动重连（指数退避）
    scheduleSSEReconnect() {
      clearTimeout(this.sseReconnectTimer);
      const delay = Math.min(2000 * Math.pow(1.5, this.sseReconnectAttempts++), 30000);
      console.log(`[SSE] 将在 ${delay}ms 后重连 (第${this.sseReconnectAttempts}次)`);
      this.sseReconnectTimer = setTimeout(() => {
        this.connectSSE();
      }, delay);
    },
    // 处理 SSE 推送的默认消息
    handleSSEMessage(data) {
      // console.log('[SSE] 处理消息:', JSON.stringify(data));
      // 根据消息类型分发处理
      if (data.event) {
        const eventType = data.event;
        const messageData = data.data;
        
        switch (eventType) {
          case 'offline_message':
            this.handleOfflineMessage(messageData);
            break;
          case 'connected':
            this.handleConnected(messageData);
            break;
          case 'ping':
            this.handlePing(messageData);
            break;
          case 'task_failed':
            this.handleTaskFailed(messageData);
            break;
          case 'task_completed':
            this.handleTaskCompleted(messageData);
            break;
          case 'business_count':
            this.handleBusinessCount(messageData);
            break;
          default:
            console.log('[SSE] 未知事件类型:', eventType);
        }
      } else if (data.task_id) {
        // 如果包含 task_id，添加到通知队列
        console.log('[SSE] 任务通知:', data);
      } else if (data.message) {
        // 普通消息推送
        console.log('[SSE] 普通消息:', data.message);
      }
    },
    // 处理离线消息数据
    handleOfflineMessage(data) {
      // 示例数据: {"event": "business_count", "data": {"business_id":14938, "business_type":"analyse_file", "msg_count":1}}
      // console.log('[SSE] 处理离线消息:', data);
      // 如果是business_count事件，通过eventBus发送消息
      if (data.event === 'business_count' && data.data) {
        // 通过事件总线通知Sidebar更新未读消息数
        eventBus.$emit('updateBusinessCount', data.data);
      }
    },
    // 处理连接成功数据
    handleConnected(data) {
      // 示例数据: {"status":"connected","user_id":17040}
      // console.log('[SSE] 连接成功:', data);
    },
    // 处理 ping 数据
    handlePing(data) {
      // 示例数据: {"timestamp":"2026-06-09T09:59:01.984047"}
      // console.log('[SSE] 心跳:', data);
    },
    // 处理任务失败数据
    handleTaskFailed(data) {
      // 示例数据: {"task_id":"1-2-3-4","status":"failed","error":"任务失败","message":"Task failed: 任务无效"}
      // console.log('[SSE] 任务失败:', data);
      // 可以在这里显示错误通知
    },
    // 处理任务完成数据
    handleTaskCompleted(data) {
      // 示例数据: {"task_id":"1-2-3-4","status":"completed","error":"","message":"Task completed"}
      // console.log('[SSE] 任务完成:', data);
      // 可以在这里更新任务状态或显示通知
    },
    // 处理业务统计数据
    handleBusinessCount(data) {
      // 示例数据: {"business_id":"123","business_type":"analyse_file","msg_count":4}
      // 通过事件总线通知 Sidebar 更新未读消息数
      eventBus.$emit('updateBusinessCount', data);
      // 如果有未读消息，触发无感知更新
      if (data.msg_count > 0 && data.business_id) {
        const currentChatId = this.$route.query.chatId;
        // 如果是新对话页面（没有currentChatId），直接触发更新；否则判断是否匹配
        if (!currentChatId || String(currentChatId) === String(data.business_id)) {
          eventBus.$emit('refreshChatHistory', data.business_id);
        }
      }
    },
    async updateDesktopTitle() {
      try {
        const version = localStorage.getItem('version');
        // 兼容无版本号的情况
        const title = version ? `汇AI ${version}` : '汇AI';

        // 🎯 Tauri v2 更新原生标题栏的正确方法
        await getCurrentWindow().setTitle(title);
        console.log('✅ 桌面端标题已更新:', title);
      } catch (error) {
        console.error(' 更新 Tauri 窗口标题失败:', error);
      }
    }
  }
};
</script>
